import { ChangeEventHandler, useId, useMemo, useState } from 'react';
import usePatchTemplate from 'hooks/services/templates/usePatchTemplate';
import useCreateTemplate from 'hooks/services/templates/useCreateTemplate';
import usePatchStrategyStep from 'hooks/services/strategy/usePatchStrategyStep';
import { Template } from 'crono-fe-common/types/template';
import useAddStrategyStepTemplate from 'hooks/services/strategy/useAddStrategyStepTemplate';
import useDeleteStrategyStepTemplate from 'hooks/services/strategy/useDeleteStrategyStepTemplate';
import { FileContent } from 'use-file-picker';
import { CronoAttachment } from 'crono-fe-common/types/cronoAttachment';
import { isFileContent } from 'pages/templates/model';
import useDeleteTemplateAttachment from 'hooks/services/templates/useDeleteTemplateAttachment';
import useAddTemplateAttachment from 'hooks/services/templates/useAddTemplateAttachment';
import { useConditionalSnackBar } from 'context/snackbar';
import { formatMissingVariableBrackets, getError } from 'crono-fe-common/utils';
import { useAnalytics } from 'context/analytics';
import { formatHtmlContent } from 'utils/formatUtils';
import { replaceAllTemplateVariables } from 'crono-fe-common/utils';
import usePatchConditionalStrategyStep from 'hooks/services/strategy/usePatchConditionalStrategyStep';
import { useAuth } from 'context/auth';
import { TagInsert } from 'crono-fe-common/types/tagInsert';
import useGetExternalProperty from 'hooks/services/externalProperty/useGetExternalProperty';
import TemplateTagSelects from './TemplateTagSelects';
import useSendPreviewEmail, {
  SendPreviewEmailDto,
} from 'hooks/services/email/useSendPreviewEmail';
import useGetTemplateVariables from 'hooks/services/variables/useGetTemplateVariables';
import useGetTemplateVariableExternalProperties from 'hooks/services/externalProperty/useGetTemplateVariableExternalProperties';
import useEmailIntegrationGuard from 'hooks/guards/useEmailIntegrationGuard';
import { MOCK_PROSPECT, MOCK_ACCOUNT } from 'pages/templates/template.mocks';
import LanguagePicker from 'components/LanguagePicker';
import { LanguageOption } from 'crono-fe-common/types/languageOption';
import TemplateType from 'crono-fe-common/types/enums/templateType';
import useGetStrategy, {
  useInvalidateGetStrategy,
} from 'hooks/services/strategy/useGetStrategy';
import { StrategyWithAnalytics } from 'crono-fe-common/types/strategy';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EditMdIcon,
  EmailMdIcon,
  Input,
  PlusMdIcon,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import { cn } from 'utils/style-utils';
import { Constants } from 'crono-fe-common/constants/constants';

const TAG_TABLE_TYPES = ['Account', 'Prospect', 'Lead'] as const;

const selectableLanguages: readonly string[] = Constants.templateLanguagesInput;

const toLanguageOption = (language: string) =>
  (selectableLanguages.includes(language)
    ? language
    : Constants.defaultLanguage) as LanguageOption;

interface EditableTemplateModalProps {
  createNewTemplate: boolean;
  stepId: number;
  conditionalStepId?: number;
  template: Template;
  content: string;
  subject: string;
  attachments: (FileContent | CronoAttachment)[];
  initialAttachments: CronoAttachment[];
  close: () => void;
  onSuccess: (
    data:
      | {
          newInsertedTemplateId: number;
          isAbTesting: boolean;
        }
      | undefined,
  ) => void;
  isABTesting: boolean;
  isConditional: boolean;
  strategyId?: number;
}

const EditableTemplateModal = ({
  createNewTemplate,
  content,
  close,
  stepId,
  conditionalStepId,
  template,
  subject,
  attachments,
  onSuccess,
  isABTesting,
  isConditional,
  initialAttachments,
  strategyId,
}: EditableTemplateModalProps) => {
  const { user } = useAuth();
  const templateNameId = useId();
  const languageSelectId = useId();
  const privateSwitchId = useId();
  const [title, setTitle] = useState<string>(template.title);

  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption>(() =>
    toLanguageOption(template.language),
  );

  const { data: externalPropertyTags } = useGetExternalProperty({
    tableType: null,
    onlyTag: true,
  });
  const [tagSelected, setTagSelected] = useState<TagInsert[]>(() =>
    (template.templateTags ?? []).map(({ externalPropertyId, value }) => ({
      externalPropertyId,
      value,
    })),
  );

  const tagProperties = useMemo(
    () =>
      TAG_TABLE_TYPES.flatMap((tableType) =>
        (externalPropertyTags?.data?.data ?? []).filter(
          (tag) => tag.tableType === tableType,
        ),
      ),
    [externalPropertyTags],
  );

  const handleTagChange = (
    externalPropertyId: number,
    value: string | null,
  ) => {
    setTagSelected((current) => {
      const others = current.filter(
        (tag) => tag.externalPropertyId !== externalPropertyId,
      );
      return value ? [...others, { externalPropertyId, value }] : others;
    });
  };

  const [error, setError] = useState<boolean>(false);

  const { data: strategyResponse } = useGetStrategy(strategyId);
  const strategy =
    strategyResponse?.data.data ?? ({ id: -1 } as StrategyWithAnalytics);

  const { mutateAsync: patchTemplateAsync, error: patchTemplateError } =
    usePatchTemplate();

  const { mutateAsync: createTemplate, error: createTemplateError } =
    useCreateTemplate();

  const { mutateAsync: patchStrategyStepAsync } = usePatchStrategyStep();

  const { mutateAsync: patchConditionalStrategyStepAsync } =
    usePatchConditionalStrategyStep();

  const { mutate: deleteStrategyStepTemplate } =
    useDeleteStrategyStepTemplate();

  const { mutateAsync: addStrategyStepTemplateAsync } =
    useAddStrategyStepTemplate();

  const { mutateAsync: deleteAttachment, error: deleteAttachmentError } =
    useDeleteTemplateAttachment();

  const { mutateAsync: addAttachmentToTemplate, error: addAttachmentError } =
    useAddTemplateAttachment();

  const analytics = useAnalytics();

  const invalidateGetStrategy = useInvalidateGetStrategy();

  const handleEdit = async () => {
    await patchTemplateAsync({
      id: template.id,
      title,
      content: formatHtmlContent(content),
      subject: formatMissingVariableBrackets(subject),
      shared: isShared,
      language: selectedLanguage,
      templateTags: tagProperties.map((tag) => ({
        externalPropertyId: tag.id,
        value:
          tagSelected.find((selected) => selected.externalPropertyId === tag.id)
            ?.value ?? null,
      })),
    });
    onSuccess(undefined);
    if (analytics) {
      analytics.track('edit-template', {
        fromCustomization: true,
      });
    }
    const attachmentsToDelete = initialAttachments.filter(
      (ia) => !attachments.some((a) => !isFileContent(a) && a.id === ia.id),
    );
    const promises = [];
    for (const attachment of attachmentsToDelete) {
      promises.push(
        deleteAttachment({
          templateId: template.id,
          attachmentId: attachment.id,
        }),
      );
    }
    await Promise.all(promises);

    const attachmentsToAdd = attachments.filter((a) =>
      isFileContent(a),
    ) as FileContent[];
    await addAttachmentToTemplate({
      templateId: template.id,
      attachments: attachmentsToAdd,
    });

    invalidateGetStrategy({ strategyId: strategyId! });
  };

  const [isShared, setIsShared] = useState<boolean>(template.shared ?? true);

  const handleCreate = async () => {
    if (title === template.title) {
      setError(true);
    } else {
      const oldAttachments = attachments.filter(
        (attachment) => !isFileContent(attachment),
      ) as CronoAttachment[];

      const newAttachments = attachments.filter((attachment) =>
        isFileContent(attachment),
      ) as FileContent[];

      const { data: response } = await createTemplate({
        ...template,
        content: formatHtmlContent(content),
        language: selectedLanguage,
        templateTags: tagSelected,
        title,
        shared: isShared,
        subject: formatMissingVariableBrackets(subject),
        attachments: newAttachments,
        attachmentIds: oldAttachments.map((attachment) => attachment.id),
      });
      if (analytics) {
        analytics.track('create-template', {
          fromCustomization: true,
        });
      }
      if (response?.data?.id) {
        const createdTemplateId = response?.data?.id;

        if (isABTesting) {
          const responseAdd = await addStrategyStepTemplateAsync({
            strategyStepId: stepId,
            ...(isConditional && conditionalStepId
              ? { strategyStepConditionalId: conditionalStepId }
              : {}),
            templateId: createdTemplateId,
            templateType: template.type,
          });

          if (responseAdd?.data?.data) {
            let strategyStepTemplateId = null;
            if (isConditional && conditionalStepId) {
              strategyStepTemplateId = strategy.steps
                .find(
                  (step) =>
                    step.strategyStepConditional?.id === conditionalStepId,
                )
                ?.strategyStepConditional?.strategyStepTemplates?.find(
                  (st) => st.template?.id === template.id,
                )?.id;
            } else {
              strategyStepTemplateId = strategy.steps
                .find((step) => step.id === stepId)
                ?.strategyStepTemplates?.find(
                  (st) => st.template?.id === template.id,
                )?.id;
            }

            if (strategyStepTemplateId) {
              deleteStrategyStepTemplate({
                strategyStepTemplateId,
              });
            }
          }
          onSuccess({
            newInsertedTemplateId: createdTemplateId,
            isAbTesting: true,
          });
        } else if (isConditional) {
          const step = strategy.steps.find((step) => step.id === stepId);
          if (step && step.strategyStepConditional) {
            await patchConditionalStrategyStepAsync({
              strategyStepId: stepId,
              templateId: response?.data?.id,
            });
            onSuccess(undefined);
          }
        } else {
          await patchStrategyStepAsync({
            id: stepId,
            strategyId: strategy.id,
            templateId: response?.data?.id,
          });
          onSuccess({
            newInsertedTemplateId: createdTemplateId,
            isAbTesting: false,
          });
        }
      }
    }
  };

  const handleTitleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setTitle(e.currentTarget.value);
    setError(false);
  };

  const handleTitleBlur = () => {
    if (!title) {
      setTitle(template.title);
    }
  };

  useConditionalSnackBar([
    {
      condition: !!createTemplateError,
      message: getError(createTemplateError) ?? 'Error creating a template',
      severity: 'error',
    },
    {
      condition: !!patchTemplateError,
      message:
        getError(patchTemplateError) ?? 'Error on saving template changes',
      severity: 'error',
    },
    {
      condition: !!deleteAttachmentError,
      message:
        getError(deleteAttachmentError) ?? 'Error on deleting attachments',
      severity: 'error',
    },
    {
      condition: !!addAttachmentError,
      message: getError(addAttachmentError) ?? 'Error on saving attachments',
      severity: 'error',
    },
  ]);

  const { data: templateVariables } = useGetTemplateVariables();
  const externalProperties = useGetTemplateVariableExternalProperties();
  const { isSuccess: emailIntegrated, data: emailData } =
    useEmailIntegrationGuard({
      verifyDefault: true,
    });

  const { mutate: sendPreviewEmail, isLoading: sendPreviewLoading } =
    useSendPreviewEmail();

  const handleSendPreview = () => {
    let message = content;

    if (user && emailIntegrated && emailData?.data?.data?.email) {
      subject = replaceAllTemplateVariables(
        subject,
        MOCK_PROSPECT,
        MOCK_ACCOUNT,
        user ?? null,
        templateVariables?.data?.data ?? null,
        externalProperties,
        true,
      );

      message = replaceAllTemplateVariables(
        message,
        MOCK_PROSPECT,
        MOCK_ACCOUNT,
        user ?? null,
        templateVariables?.data?.data ?? null,
        externalProperties,
        true,
      );

      message = formatHtmlContent(message);

      const sendEmailDto: SendPreviewEmailDto = {
        emailInfo: {
          subject: subject,
          contentHtml: message,
        },
      };

      sendPreviewEmail(sendEmailDto);

      if (analytics) {
        analytics.track('send-email-preview', {});
      }
    }
  };

  const canSendTest = template.type === TemplateType.EMAIL;

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) close();
      }}
    >
      <DialogContent className="h-131.5 max-w-131 gap-6">
        <DialogHeader>
          <DialogTitle>Save template as</DialogTitle>
        </DialogHeader>

        <DialogBody className="gap-6">
          <div className="flex flex-col gap-2">
            <label htmlFor={templateNameId} className="text-sm font-semibold">
              Template name*
            </label>

            <Input
              id={templateNameId}
              placeholder="Template name"
              value={title}
              onBlur={handleTitleBlur}
              onChange={handleTitleChange}
              aria-invalid={error}
            />

            {!createNewTemplate && (
              <p
                className={cn(
                  'text-xs',
                  error ? 'text-red-500' : 'text-gray-700',
                )}
              >
                To save as a new template, use a different name
              </p>
            )}
          </div>

          <div className="flex w-full flex-col gap-2">
            <label htmlFor={languageSelectId} className="text-sm font-semibold">
              Language
            </label>

            <LanguagePicker
              language={selectedLanguage}
              onLanguageChange={setSelectedLanguage}
              triggerClassName="w-full"
              triggerId={languageSelectId}
            />
          </div>

          {tagProperties.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold">Add tags:</p>

              <TemplateTagSelects
                tags={tagProperties}
                selected={tagSelected}
                onTagChange={handleTagChange}
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <label
              htmlFor={privateSwitchId}
              className="cursor-pointer text-sm font-medium text-gray-700"
            >
              Private
            </label>

            <Switch
              id={privateSwitchId}
              checked={!isShared}
              onCheckedChange={(checked) => setIsShared(!checked)}
            />
          </div>
        </DialogBody>

        <DialogFooter className="flex-row items-center">
          {canSendTest && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    onClick={handleSendPreview}
                    disabled={!emailIntegrated}
                    loading={sendPreviewLoading}
                    size="lg"
                    variant="text"
                    startIcon={EmailMdIcon}
                  >
                    Send test
                  </Button>
                </span>
              </TooltipTrigger>

              {!emailIntegrated && (
                <TooltipContent>
                  Please set a default email and connect it to send a test email
                </TooltipContent>
              )}
            </Tooltip>
          )}

          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="lg"
              className="px-3"
              startIcon={PlusMdIcon}
              onClick={handleCreate}
            >
              Create new
            </Button>

            {!createNewTemplate && (
              <Button
                variant="outline"
                size="lg"
                className="px-3"
                startIcon={EditMdIcon}
                onClick={handleEdit}
              >
                Edit existing
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditableTemplateModal;
